import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Dumbbell, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { trackStartFreeConversion, logEvent } from "@/lib/analytics";
import useNoIndex from "@/hooks/useNoIndex";
import { toast } from "sonner";

export default function Register() {
  useNoIndex();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    logEvent("signup_started", {});
    try {
      const u = await register(name, email, password);
      trackStartFreeConversion("register");
      logEvent("signup_completed", {});
      if (u && u.email_verified === false) {
        logEvent("verify_email_sent", { source: "signup" });
        toast.success("Account created. Let's get to know you.");
      } else {
        toast.success("You're in. Let's get to work.");
      }
      // New signups begin with The First Workout (it routes onward to verify/dashboard afterwards).
      navigate("/app/first-workout");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain relative flex flex-col">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 0%, rgba(0,229,255,0.15), transparent 40%), radial-gradient(circle at 10% 100%, rgba(255,87,34,0.18), transparent 40%)",
        }}
      />
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-md mx-auto w-full px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-center gap-2 mb-8">
            <div className="h-10 w-10 rounded-lg bg-primary grid place-items-center glow-orange">
              <Dumbbell size={20} className="text-white" />
            </div>
            <span className="heading text-3xl font-black tracking-tighter">
              UNLOAD<span className="text-primary">DAILY</span>
            </span>
          </div>

          <h1 className="heading text-5xl font-black tracking-tighter uppercase leading-none">
            Build your <span className="text-accent">strength</span>.
          </h1>
          <p className="text-muted-foreground mt-3 mb-8">
            Private. Judgment-free. Your training starts today.
          </p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Name</label>
              <input
                data-testid="register-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1 w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg focus:border-accent focus:ring-1 focus:ring-accent p-3 outline-none transition-colors"
                placeholder="What should we call you?"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                data-testid="register-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg focus:border-accent focus:ring-1 focus:ring-accent p-3 outline-none transition-colors"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                data-testid="register-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1 w-full bg-[#1A1A1A] border border-white/10 text-white rounded-lg focus:border-accent focus:ring-1 focus:ring-accent p-3 outline-none transition-colors"
                placeholder="At least 6 characters"
              />
            </div>
            {error && (
              <p data-testid="register-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <button
              data-testid="register-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white font-bold tracking-widest uppercase rounded-lg hover:shadow-[0_0_20px_rgba(255,87,34,0.5)] transition-all py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Start Training"}
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Already have an account?{" "}
            <Link data-testid="go-login-link" to="/app/login" className="text-accent font-semibold hover:underline">
              Log in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
